import { Link } from 'react-router-dom';
import {
  telHref, formatPhone, buildWhatsAppUrl, whatsappMessage, formatRupees,
  type PageSection, type SiteSettings, type Branch, type MediaRef,
} from '@cng/shared';
import { SmartImage, SmartVideo } from '@/components/common/SmartImage';
import { LocationsSection } from '@/components/locations/LocationsSection';

interface RenderContext {
  settings: SiteSettings;
  branches: Branch[];
  /**
   * Page slug, used as the CSS scope for migrated HTML blocks. The old site's
   * landing pages ship their own reset (`* { margin: 0; padding: 0 }`), so the
   * scope has to sit on the block itself — put it on the whole page and that
   * reset flattens the shared header, locations and footer components too.
   */
  scopeKey?: string;
}

/** Section payloads are stored as loose JSON; these readers keep access honest. */
const str = (d: Record<string, unknown>, key: string, fallback = ''): string =>
  typeof d[key] === 'string' ? (d[key] as string) : fallback;
const arr = <T,>(d: Record<string, unknown>, key: string): T[] =>
  Array.isArray(d[key]) ? (d[key] as T[]) : [];
const media = (d: Record<string, unknown>, key: string): MediaRef | null =>
  (d[key] && typeof d[key] === 'object' ? (d[key] as MediaRef) : null);

/**
 * Renders one CMS-defined section. Landing pages are built from these blocks
 * rather than raw HTML, so the owner edits structured fields and cannot break
 * the page layout.
 */
export function SectionRenderer({ section, ctx }: { section: PageSection; ctx: RenderContext }) {
  if (!section.enabled) return null;
  const d = section.data ?? {};
  const { settings, branches, scopeKey } = ctx;

  switch (section.type) {
    case 'hero': {
      const image = media(d, 'image');
      return (
        <section className="cng-section cng-section-hero">
          <div className="cng-container">
            {str(d, 'eyebrow') ? <span className="cng-eyebrow">{str(d, 'eyebrow')}</span> : null}
            <h1>{str(d, 'title')}</h1>
            {str(d, 'subtitle') ? <p className="cng-section-sub">{str(d, 'subtitle')}</p> : null}
            {arr<{ label: string; href: string; style?: string }>(d, 'ctas').length > 0 && (
              <div className="cng-section-ctas">
                {arr<{ label: string; href: string; style?: string }>(d, 'ctas').map((cta) => (
                  <CtaLink key={cta.label} {...cta} />
                ))}
              </div>
            )}
          </div>
          {image ? (
            <div className="cng-section-hero-media">
              <SmartImage media={image} alt={image.alt ?? str(d, 'title')} loading="eager" sizes="100vw" />
            </div>
          ) : null}
        </section>
      );
    }

    case 'richText': {
      const html = str(d, 'html');
      // A block migrated from a landing page brings its own layout and
      // stylesheet, so it renders full-bleed. Wrapping it in the standard
      // container would fight the CSS it was written against.
      // The markup is sanitised server-side before it is stored.
      // `contained` marks markup this migration generated — a list or a text
      // widget — which has no stylesheet of its own and belongs in the site's
      // container. Only the raw HTML widgets lifted from the original render
      // full-bleed against their own CSS.
      if (html && !d.contained) {
        return (
          <div
            className="cng-section-html"
            data-page={scopeKey}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        );
      }
      if (html) {
        return (
          <section className="cng-section cng-section-rich">
            <div className="cng-container">
              {str(d, 'title') ? <h2>{str(d, 'title')}</h2> : null}
              <div className="cng-rich" dangerouslySetInnerHTML={{ __html: html }} />
            </div>
          </section>
        );
      }
      return (
        <section className="cng-section cng-section-rich">
          <div className="cng-container">
            {str(d, 'title') ? <h2>{str(d, 'title')}</h2> : null}
            <div className="cng-rich">
              {str(d, 'body').split('\n\n').filter(Boolean).map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
            </div>
          </div>
        </section>
      );
    }

    case 'imageGrid': {
      const images = arr<MediaRef>(d, 'images');
      if (!images.length) return null;
      const columns = str(d, 'columns', '3');
      // One picture on its own is not a thumbnail — it keeps its own proportions
      // and its own size. See `.cng-image-grid.is-single` in enhancements.css.
      const single = columns === '1' || images.length === 1;
      return (
        <section className="cng-section cng-section-grid">
          <div className="cng-container">
            {str(d, 'title') ? <h2>{str(d, 'title')}</h2> : null}
            <div
              className={`cng-image-grid${single ? ' is-single' : ''}`}
              style={{ '--cols': columns } as React.CSSProperties}
            >
              {images.map((m, i) => (
                <SmartImage
                  key={m.url ?? i}
                  media={m}
                  alt={m.alt ?? ''}
                  sizes={single ? '(max-width: 1250px) 100vw, 1250px' : '(max-width: 768px) 50vw, 320px'}
                />
              ))}
            </div>
          </div>
        </section>
      );
    }

    case 'services': {
      const rows = arr<{ name: string; description?: string; price?: string }>(d, 'items');
      if (!rows.length) return null;
      return (
        <section className="cng-section cng-section-services">
          <div className="cng-container">
            {str(d, 'title') ? <h2>{str(d, 'title')}</h2> : null}
            <div className="cng-service-list">
              {rows.map((row) => (
                <div className="service-row" key={row.name}>
                  <div className="service-info">
                    <span className="service-name">{row.name}</span>
                    {row.description ? <div className="service-desc">{row.description}</div> : null}
                  </div>
                  <div className="service-price">{row.price}</div>
                </div>
              ))}
            </div>
            {str(d, 'note') ? <p className="cng-section-note">{str(d, 'note')}</p> : null}
          </div>
        </section>
      );
    }

    case 'offers': {
      const offers = arr<{ title: string; price?: number; displayPriceText?: string; badge?: string }>(d, 'items');
      if (!offers.length) return null;
      return (
        <section className="cng-section cng-section-offers">
          <div className="cng-container">
            {str(d, 'title') ? <h2>{str(d, 'title')}</h2> : null}
            <div className="cng-offer-cards">
              {offers.map((o) => (
                <div className="cng-offer-card" key={o.title}>
                  {o.badge ? <span className="cng-offer-badge">{o.badge}</span> : null}
                  <h3>{o.title}</h3>
                  <span className="cng-offer-price">{o.displayPriceText || formatRupees(o.price ?? null)}</span>
                  <a
                    className="cng-offer-cta"
                    href={buildWhatsAppUrl({
                      phone: settings.whatsappNumber || settings.primaryPhone,
                      message: whatsappMessage(
                        'offerBooking',
                        { name: o.title, price: o.price ?? '' },
                        settings.whatsappTemplates,
                      ),
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book
                  </a>
                </div>
              ))}
            </div>
            {str(d, 'note') ? <p className="cng-section-note">{str(d, 'note')}</p> : null}
          </div>
        </section>
      );
    }

    case 'testimonials': {
      const quotes = arr<{ quote: string; name: string; label?: string; rating?: number }>(d, 'items');
      if (!quotes.length) return null;
      return (
        <section className="cng-section cng-section-testimonials">
          <div className="cng-container">
            {str(d, 'title') ? <h2>{str(d, 'title')}</h2> : null}
            <div className="cng-quote-grid">
              {quotes.map((q) => (
                <blockquote className="cng-quote-card" key={q.quote.slice(0, 40)}>
                  <span className="cng-quote-stars" aria-label={`Rated ${q.rating ?? 5} out of 5`}>
                    <span aria-hidden="true">{'★'.repeat(Math.round(q.rating ?? 5))}</span>
                  </span>
                  <p>{q.quote}</p>
                  <footer>
                    <cite>{q.name}</cite>
                    {q.label ? <span> — {q.label}</span> : null}
                  </footer>
                </blockquote>
              ))}
            </div>
          </div>
        </section>
      );
    }

    case 'branches':
      return (
        <LocationsSection
          config={{
            enabled: true,
            tag: str(d, 'tag'),
            title: str(d, 'title'),
            titleAccent: str(d, 'titleAccent'),
            subtitle: str(d, 'subtitle'),
          }}
          branches={str(d, 'branchSlug') ? branches.filter((b) => b.slug === str(d, 'branchSlug')) : branches}
          settings={settings}
        />
      );

    case 'cta': {
      const image = media(d, 'image');
      return (
        <section className="cng-section cng-section-cta">
          <div className="cng-container">
            {image ? <SmartImage media={image} alt={image.alt ?? ''} sizes="(max-width: 768px) 100vw, 480px" /> : null}
            <div>
              <h2>{str(d, 'title')}</h2>
              {str(d, 'body') ? <p>{str(d, 'body')}</p> : null}
              <div className="cng-section-ctas">
                {arr<{ label: string; href: string; style?: string }>(d, 'ctas').map((cta) => (
                  <CtaLink key={cta.label} {...cta} />
                ))}
                {str(d, 'phone') ? (
                  <a className="cng-btn cng-btn-primary" href={telHref(str(d, 'phone'))}>
                    Call {formatPhone(str(d, 'phone'))}
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      );
    }

    case 'customSeoContent':
      return (
        <section className="cng-section cng-section-seo">
          <div className="cng-container">
            {str(d, 'title') ? <h2>{str(d, 'title')}</h2> : null}
            {arr<{ heading?: string; body: string }>(d, 'blocks').map((b) => (
              <div className="cng-seo-block" key={(b.heading ?? '') + b.body.slice(0, 30)}>
                {b.heading ? <h3>{b.heading}</h3> : null}
                {b.body.split('\n\n').filter(Boolean).map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
              </div>
            ))}
          </div>
        </section>
      );

    case 'gallery': {
      const images = arr<MediaRef>(d, 'images');
      if (!images.length) return null;
      return (
        <section className="cng-section cng-section-gallery">
          <div className="cng-container">
            {str(d, 'title') ? <h2>{str(d, 'title')}</h2> : null}
            <div className="cng-image-grid" style={{ '--cols': '4' } as React.CSSProperties}>
              {images.map((m, i) => (
                <SmartImage key={m.url ?? i} media={m} alt={m.alt ?? ''} sizes="(max-width: 768px) 50vw, 280px" />
              ))}
            </div>
          </div>
        </section>
      );
    }

    case 'featuredWork': {
      const videos = arr<{ title: string; video: MediaRef; poster?: MediaRef; category?: string }>(d, 'items');
      if (!videos.length) return null;

      // A video migrated from inside a landing page is a single clip the reader
      // chooses to play, the way the original had it — not one card in the
      // homepage's autoplaying wall.
      if (str(d, 'variant') === 'inline') {
        return (
          <section className="cng-section cng-section-video-inline">
            <div className="cng-container">
              {str(d, 'title') ? <h2>{str(d, 'title')}</h2> : null}
              <div className="cng-inline-videos">
                {videos.map((v, i) => (
                  <SmartVideo
                    key={v.video?.url ?? i}
                    media={v.video}
                    poster={v.poster?.url ?? null}
                    ariaLabel={v.title}
                    autoPlay={false}
                    controls
                  />
                ))}
              </div>
            </div>
          </section>
        );
      }

      return (
        <section className="cng-section cng-section-video">
          <div className="cng-container">
            {str(d, 'title') ? <h2>{str(d, 'title')}</h2> : null}
            <div className="cng-video-row">
              {videos.map((v) => (
                <figure key={v.title}>
                  <SmartVideo media={v.video} poster={v.poster?.url ?? null} ariaLabel={v.title} />
                  <figcaption>{v.title}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      );
    }

    // Sections that only make sense on their dedicated page templates.
    case 'ticker':
    case 'serviceMenu':
    case 'stats':
    case 'curatedServices':
    case 'servicePills':
    case 'imageCarousel':
    case 'packages':
    case 'transformations':
      return null;

    default:
      return null;
  }
}

function CtaLink({ label, href, style }: { label: string; href: string; style?: string }) {
  const className = `cng-btn ${style === 'secondary' ? 'cng-btn-secondary' : 'cng-btn-primary'}`;
  if (href.startsWith('/') && !href.startsWith('//')) {
    return <Link className={className} to={href}>{label}</Link>;
  }
  const external = /^https?:/i.test(href);
  return (
    <a
      className={className}
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
    >
      {label}
    </a>
  );
}
